import React, { useRef, useState } from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import PasskeyRegistrationCard from '@devnw/cyber-iam/auth/PasskeyRegistrationCard/PasskeyRegistrationCard'
// eslint-disable-next-line import/no-unresolved
import TotpEnrollmentPanel from '@devnw/cyber-iam/auth/TotpEnrollmentPanel/TotpEnrollmentPanel'
import type {
  RegisterBeginOptions,
  RegisterCompletePayload,
} from '@devnw/cyber-iam/auth/protocols/webauthn'
import type { SetupClient } from '../../../api/setupClient'
import { renderTotpQr } from '../totpQr'
import { friendlySetupError } from '../setupErrors'
import {
  StepContainerWide,
  StepHeading,
  StepDescription,
  ErrorAlert,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
} from '../setupStyled'

interface EnrollMFAProps {
  client: SetupClient
  onNext: () => void
  onBack: () => void
}

/**
 * Third step: enroll the admin's first authentication factors.
 *
 * SECURITY INVARIANT (binding — see phaseE-brief.md): at least one
 * phishing-resistant factor (WebAuthn/FIDO2 passkey, hardware security key,
 * or HSM-backed credential) is REQUIRED before the wizard can finish. TOTP is
 * intentionally treated as an ADDITIONAL AAL2 factor, never a substitute —
 * completing TOTP enrollment alone must NOT enable Next.
 *
 * The Next gate below tracks ONLY the WebAuthn ceremony outcome
 * (webauthnConfirmed). A managed mTLS client certificate ALSO satisfies the
 * server's completeInit precondition (hasPhishingResistantFactor's cert
 * branch), but a browser page cannot observe or "confirm" a transport-layer
 * client certificate, so this step cannot unlock Next from an mTLS cert alone —
 * an mTLS-only admin registers a passkey here to advance. The copy below is
 * deliberately honest about that rather than implying an mTLS certificate lets
 * the operator skip this step.
 *
 * FOLLOW-UP (deferred — not built here to avoid widening this fix's scope):
 * expose a setup-token-scoped enrollment-status check the wizard can poll to
 * unlock Next when InitState.AdminSub already has a bound phishing-resistant
 * factor (e.g. an mTLS cert), so an mTLS-only admin need not also register a
 * passkey. See webauthn_enrollment_status.go / hasPhishingResistantFactor.
 *
 * This step renders NO SMS/email/text-message fallback option at all (per
 * the brief's binding constraint) — those factors are phishable and are not
 * offered anywhere in this UI, not even as a "skip for now" escape hatch.
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI Card/Chip/TextField onto
 * `@devnw/cyber-iam/auth/PasskeyRegistrationCard` (owns the
 * navigator.credentials.create(...) ceremony internally via its bundled
 * `runRegistrationCeremony`, mirroring the WebAuthnCredentials page's
 * Phase-2a migration — see gate6-phase2a-report.md) and
 * `@devnw/cyber-iam/auth/TotpEnrollmentPanel` (renders the QR/secret/code
 * input; this page still owns every setupClient API call and the
 * webauthnConfirmed/totpPending gating that unlocks Next).
 */
export const EnrollMFA = ({
  client,
  onNext,
  onBack,
}: EnrollMFAProps): JSX.Element => {
  const [webauthnConfirmed, setWebauthnConfirmed] = useState(false)
  // Hand-off between begin() and complete(): PasskeyRegistrationCard always
  // fully resolves begin() before invoking the ceremony and then complete(),
  // and its own reentrancy guard rules out a second overlapping run, so a
  // plain ref is a safe way to carry the session ID from begin() to complete().
  const sessionIdRef = useRef('')

  const [totpUri, setTotpUri] = useState<string | null>(null)
  const [totpSecret, setTotpSecret] = useState<string | null>(null)
  const [totpQrDataUri, setTotpQrDataUri] = useState<string | null>(null)
  const [totpStatus, setTotpStatus] = useState<'idle' | 'working' | 'error'>(
    'idle'
  )
  const [totpError, setTotpError] = useState<string | null>(null)
  const [totpVerified, setTotpVerified] = useState(false)

  const handleWebauthnBegin = async (): Promise<RegisterBeginOptions> => {
    try {
      const begin = await client.webauthnRegisterBegin()
      sessionIdRef.current = begin.session_id
      return begin.options as unknown as RegisterBeginOptions
    } catch (e: unknown) {
      throw new Error(
        friendlySetupError(e, 'Failed to start passkey registration')
      )
    }
  }

  const handleWebauthnComplete = async (
    payload: RegisterCompletePayload
  ): Promise<void> => {
    try {
      await client.webauthnRegisterComplete({
        sessionId: sessionIdRef.current,
        credential: payload,
      })
    } catch (e: unknown) {
      throw new Error(friendlySetupError(e, 'Passkey registration failed'))
    }
  }

  const handleStartTotp = async (): Promise<void> => {
    setTotpStatus('working')
    setTotpError(null)
    try {
      const begin = await client.totpEnrollBegin()
      setTotpUri(begin.otpauth_uri)
      setTotpSecret(begin.secret)
      try {
        const dataUri = await renderTotpQr(begin.otpauth_uri)
        setTotpQrDataUri(dataUri)
      } catch {
        // QR rendering is a convenience — the manual-entry secret above still
        // works, so a render failure must not block enrollment.
        setTotpQrDataUri(null)
      }
      setTotpStatus('idle')
    } catch (e: unknown) {
      setTotpError(friendlySetupError(e, 'Failed to start TOTP enrollment'))
      setTotpStatus('error')
    }
  }

  const handleVerifyTotp = async (code: string): Promise<void> => {
    try {
      await client.totpEnrollVerify(code)
      setTotpVerified(true)
    } catch (e: unknown) {
      throw new Error(friendlySetupError(e, 'Invalid code — please try again'))
    }
  }

  // Discard a STARTED-but-unverified TOTP enrollment so the operator can advance
  // without being trapped by the pending-enrollment Next gate below. Resets the
  // enrollment back to its untouched ('idle', no otpauth URI) state.
  const handleCancelTotp = (): void => {
    setTotpUri(null)
    setTotpSecret(null)
    setTotpQrDataUri(null)
    setTotpStatus('idle')
    setTotpError(null)
  }

  // A TOTP enrollment that has been STARTED (otpauth URI issued) but not yet
  // verified. Advancing while this is pending would silently abandon the
  // half-finished enrollment, so Next is blocked until it is verified or
  // explicitly cancelled. An untouched enrollment (totpUri === null) is NOT
  // pending and never blocks Next.
  const totpPending = totpUri !== null && !totpVerified

  return (
    <StepContainerWide>
      <StepHeading>Enroll multi-factor authentication</StepHeading>
      <StepDescription>
        At least one phishing-resistant factor is required before setup can
        finish. Register a passkey or security key below to continue. (A managed
        mTLS client certificate also satisfies this requirement at the server,
        but this browser step still needs a passkey registration to advance.)
        TOTP can be added as an additional factor but does not satisfy this
        requirement on its own.
      </StepDescription>

      <PasskeyRegistrationCard
        testID="enroll-mfa-passkey"
        title="Passkey / security key"
        description="Phishing-resistant. Registering one here satisfies the required factor and unlocks the rest of setup."
        buttonLabel="Register a passkey or security key"
        begin={handleWebauthnBegin}
        complete={handleWebauthnComplete}
        onRegistered={() => setWebauthnConfirmed(true)}
        disabled={webauthnConfirmed}
      />

      {!totpUri && (
        <ButtonRow>
          <SecondaryButton
            type="button"
            onClick={() => handleStartTotp()}
            disabled={totpStatus === 'working'}
          >
            {totpStatus === 'working' ? 'Working…' : 'Set up authenticator app'}
          </SecondaryButton>
        </ButtonRow>
      )}
      {totpStatus === 'error' && totpError && !totpUri && (
        <ErrorAlert role="alert">{totpError}</ErrorAlert>
      )}

      {totpUri && totpSecret && (
        <>
          <TotpEnrollmentPanel
            testID="enroll-mfa-totp"
            secret={totpSecret}
            otpauthUri={totpUri}
            qrCodeDataUrl={totpQrDataUri ?? undefined}
            onVerify={handleVerifyTotp}
            verified={totpVerified}
            description="Optional. Adds a second AAL2 factor on top of your passkey — it does not by itself satisfy the phishing-resistant requirement."
          />
          {!totpVerified && (
            <ButtonRow>
              <SecondaryButton type="button" onClick={handleCancelTotp}>
                Cancel
              </SecondaryButton>
            </ButtonRow>
          )}
        </>
      )}

      {webauthnConfirmed && totpPending && (
        <StepDescription>
          Finish verifying your authenticator app, or cancel it, before
          continuing.
        </StepDescription>
      )}
      <ButtonRow>
        <SecondaryButton type="button" onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={onNext}
          disabled={!webauthnConfirmed || totpPending}
        >
          Next
        </PrimaryButton>
      </ButtonRow>
    </StepContainerWide>
  )
}
